import { useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { Box, Button, FormControl } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DateMetadata } from "../types"

dayjs.extend(isSameOrBefore)

type DateRangeSelector = {
  isLoading: boolean,
  setDateRange: (from: Dayjs, to: Dayjs) => void,
}

const DateRangeSelector = (props: DateRangeSelector) => {
  const { isLoading, setDateRange } = props

  const defaults = {
    dates: {
      from: dayjs().subtract(12, "weeks").startOf("isoWeek"),
      to: dayjs().startOf('day'),
    },
    dateValidation: {
      fromDate: { isValid: true, message: '' },
      toDate: { isValid: true, message: '' },
    }
  }

  const [fromDate, setFromDate] = useState<Dayjs>(defaults.dates.from)
  const [toDate, setToDate] = useState<Dayjs>(defaults.dates.to)
  const [dateValidation, setDateValidation] = useState(defaults.dateValidation)

  const handleShowDates = () => setDateRange(fromDate, toDate)

  const getDateValidation = (metadata: DateMetadata) => {
    const messages = {
      futureDate: 'Future Date Not Allowed',
      invalidDateRange: 'Invalid Date Order',
    }

    const isValid = !metadata.isFutureDate && metadata.isValidDateRange

    let message = ''
    if (metadata.isFutureDate) message = messages.futureDate
    else if (!metadata.isValidDateRange) message = messages.invalidDateRange

    return { isValid, message }
  }

  const validateDates = (fromDate: Dayjs, toDate: Dayjs) => {
    const today = dayjs().startOf('day')
    const isValidDateRange = fromDate.isSameOrBefore(toDate, 'day')

    const dates = {
      from: {
        isValidDateRange,
        isFutureDate: fromDate.isAfter(today, 'day'),
      },
      to: {
        isValidDateRange,
        isFutureDate: toDate.isAfter(today, 'day'),
      },
    }

    const validations = {
      fromDate: getDateValidation(dates.from),
      toDate: getDateValidation(dates.to),
    }

    return validations
  }

  const handleDateChange = (type: 'from' | 'to') => {
    const handler = (date: Dayjs | null) => {
      if (date) {
        const from = type === 'from' ? date : fromDate
        const to = type === 'to' ? date: toDate 

        const validations = validateDates(from, to)

        if (type === 'from') setFromDate(date)
        else if (type === 'to') setToDate(date)

        setDateValidation(validations)      
      }
    }

    return handler
  }

  const disableSearch = !dateValidation.fromDate.isValid || !dateValidation.toDate.isValid || isLoading

  return (
    <>
      <Box className="flex" sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
        <FormControl sx={{ width: "200px", height: "40px", marginRight: "16px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            <DatePicker
              slotProps={{
                textField: {
                  sx: {
                    "> .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  },
                  error: !dateValidation.fromDate.isValid,
                  helperText: dateValidation.fromDate.message,
                },
              }}
              sx={{ width: "200px", height: "10px" }}
              label="From"
              value={fromDate}
              onChange={handleDateChange('from')}
              format="MM-DD-YYYY"
              disableFuture
            />
          </LocalizationProvider>
        </FormControl>
        <FormControl sx={{ width: "200px", height: "40px", marginRight: "16px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            <DatePicker
              slotProps={{
                textField: {
                  sx: {
                    "> .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  },
                  error: !dateValidation.toDate.isValid,
                  helperText: dateValidation.toDate.message,
                },
              }}
              sx={{ width: "200px" }}
              label="To"
              value={toDate}
              onChange={handleDateChange('to')}
              format="MM-DD-YYYY"
              disableFuture
            />
          </LocalizationProvider>
        </FormControl>
        <Box className="flex" style={{ alignItems: "center" }}>
          <Button
            variant="contained"
            onClick={handleShowDates}
            disabled={disableSearch}
          >
            Show Dates
          </Button>
        </Box>
      </Box>
      <Box>
        <span style={{ fontSize: '13px' }}>
          This calendar displays data based on the test result date, not the test date.
        </span>
      </Box>
    </>
  )
}

export default DateRangeSelector